import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import { HiOutlineLightBulb } from "react-icons/hi2";
import {
  FaUserTie,
  FaUserSecret,
  FaUserShield,
  FaUserGraduate,
  FaRegBuilding,
  FaSitemap,
  FaHandshake,
  FaInfinity,
} from "react-icons/fa";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}

export default function CreateProjectPage() {
  const user = useMemo(() => readUserFromStorage(), []);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadCatError, setLoadCatError] = useState("");

  // form state
  const [projectName, setProjectName] = useState("");
  const [hostType, setHostType] = useState("");
  const [projectType, setProjectType] = useState("");
  const [officerType, setOfficerType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [phaseCount, setPhaseCount] = useState("");

  // config
  const hostTypes = [
    { value: "club", label: "Club Project", description: "Host Club: Leo Club of SLIIT", icon: <FaRegBuilding className="text-xl text-indigo-400" /> },
    { value: "district", label: "On behalf of a District Project", description: "", icon: <FaSitemap className="text-xl text-indigo-400" />, image: "https://www.lionsclubs.org/resources/109141433" },
    { value: "multiple", label: "On behalf of Multiple District Project", description: "", icon: <FaSitemap className="text-xl text-indigo-400" />, image: "https://www.lionsclubs.org/resources/109141433" },
  ];
  const projectTypes = [
    { value: "service", label: "Club Service Project", description: "Host Club: Leo Club of SLIIT", icon: <FaRegBuilding className="text-xl text-indigo-400" /> },
    { value: "joint", label: "Joint Project with another Leo/Non-Leo Club(s)", description: "", icon: <FaHandshake className="text-xl text-indigo-400" /> },
    { value: "continuous", label: "Continuous Project", description: "Phase options 1–10", icon: <FaInfinity className="text-xl text-indigo-400" /> },
  ];
  const officerTypes = [
    { icon: <FaUserGraduate className="text-xl text-indigo-400" />, value: "leader", label: "Project Leader" },
    { icon: <FaUserTie className="text-xl text-indigo-400" />, value: "chairperson", label: "Project Chairperson" },
    { icon: <FaUserSecret className="text-xl text-indigo-400" />, value: "secretary", label: "Project Secretary" },
    { icon: <FaUserShield className="text-xl text-indigo-400" />, value: "treasurer", label: "Project Treasurer" },
  ];

  // Phase options logic:
  // - Normal: [1,2,3]
  // - Continuous: [1..10]
  const phaseOptions = useMemo(() => {
    if (projectType === "continuous") return Array.from({ length: 10 }, (_, i) => i + 1);
    return [1, 2, 3];
  }, [projectType]);

  // Keep phaseCount valid if projectType changes
  useEffect(() => {
    if (!phaseOptions.includes(Number(phaseCount))) {
      setPhaseCount(""); // reset if out-of-range
    }
  }, [projectType]); // eslint-disable-line react-hooks/exhaustive-deps

  // load categories
  useEffect(() => {
    (async () => {
      try {
        setLoadCatError("");
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_categories.php`, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data?.success && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          setCategories([]);
          setLoadCatError(data?.message || "Failed to load categories");
        }
      } catch {
        setLoadCatError("Failed to load categories");
      }
    })();
  }, []);

  const formValid =
    projectName.trim().length >= 3 &&
    hostType &&
    projectType &&
    officerType &&
    categoryId &&
    Number(phaseCount) >= 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || submitting) return;

    const payload = {
      SLIITID: user?.SLIITID || null,
      projectName: projectName.trim(),
      hostType,
      projectType,
      officerType,
      categoryId: Number(categoryId),
      phaseCount: Number(phaseCount || 0),
    };

    try {
      setSubmitting(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/create_project.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.success) {
        setProjectName("");
        setHostType("");
        setProjectType("");
        setOfficerType("");
        setCategoryId("");
        setPhaseCount("");
        alert("Project created successfully!");
      } else {
        alert(data?.message || "Unable to create project.");
      }
    } catch (err) {
      alert("Network/Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // shared card classes without white borders/lines
  const cardBase =
    "flex items-center gap-3 p-4 rounded-xl w-full shadow-lg transition-colors bg-[#0a1324] border border-transparent hover:ring-1 hover:ring-indigo-400/30";
  const cardSelected = "bg-indigo-600/20 ring-1 ring-indigo-400/50";

  return (
    <div className="flex bg-[#0b132b] text-white min-h-screen">
      <Sidebar />
      <Topbar
        user={{
          name: user?.name || "Member",
          role: user?.role || "Member",
          image: user?.image || "/user.png",
        }}
      />

      <main className="ml-64 mt-14 w-full px-8 py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#1b2233]/80 to-[#151b28]/80 border border-white/10 rounded-2xl p-5 mb-8 flex items-center gap-4 shadow-[0_20px_60px_-30px_rgba(0,0,0,.8)]">
          <span className="flex items-center gap-3">
            <span className="bg-indigo-600/90 p-2 rounded-full border border-white/10">
              <HiOutlineLightBulb className="text-white" style={{ fontSize: "1.4em" }} />
            </span>
            <span className="text-lg font-bold tracking-wide">
              Club Project <span className="text-white/60 font-normal">/ Create Club Project</span>
            </span>
          </span>
        </div>

        <div className="bg-[#111827] rounded-2xl p-8 shadow-[0_20px_60px_-30px_rgba(0,0,0,.85)] border border-white/10">
          {/* Step 1 */}
          <div className="mb-8">
            <div className="text-indigo-300 font-semibold">Step 1 | Project Details</div>
            <div className="text-xs text-white/50 mb-4">Enter the details of your project</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Project Name */}
            <div>
              <label className="block text-sm text-white/80 mb-1">Project Name</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Ex: ගිට් කැම්ප් / GitCamp"
                className="w-full px-4 py-3 rounded-xl border border-transparent text-white bg-[#0b1220] placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="text-xs text-white/50 mt-1">Enter the name of your project</div>
            </div>

            {/* Project Host Type */}
            <div>
              <div className="text-white font-semibold">Project Host Type</div>
              <div className="text-xs text-white/50 mb-2">Select the type of project host</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {hostTypes.map((item) => (
                  <button
                    type="button"
                    key={item.value}
                    onClick={() => setHostType(item.value)}
                    className={`${cardBase} ${hostType === item.value ? cardSelected : ""}`}
                  >
                    <span>
                      {item.image ? (
                        <img src={item.image} alt={item.label} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        item.icon
                      )}
                    </span>
                    <span>
                      <div className="font-bold">{item.label}</div>
                      <div className="text-xs text-white/60">{item.description}</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Project Type */}
            <div>
              <div className="text-white font-semibold">Project Type</div>
              <div className="text-xs text-white/50 mb-2">Select the type of project (Organizing type)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {projectTypes.map((item) => (
                  <button
                    type="button"
                    key={item.value}
                    onClick={() => setProjectType(item.value)}
                    className={`${cardBase} ${projectType === item.value ? cardSelected : ""}`}
                  >
                    <span>{item.icon}</span>
                    <span>
                      <div className="font-bold">{item.label}</div>
                      <div className="text-xs text-white/60">{item.description}</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Category + Phases */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <label className="block text-sm text-white/80 mb-1">Project Category</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-transparent text-white bg-[#0b1220] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-white/50 mt-1">
                  {loadCatError ? <span className="text-red-400">{loadCatError}</span> : "One category can contain many projects"}
                </div>
              </div>

              {/* Phases */}
              <div>
                <label className="block text-sm text-white/80 mb-1">Number of Phases</label>
                <select
                  value={phaseCount}
                  onChange={(e) => setPhaseCount(Number(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl border border-transparent text-white bg-[#0b1220] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select phase count</option>
                  {phaseOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-white/50 mt-1">
                  {projectType === "continuous" ? "Continuous projects: choose 1–10 phases." : "Default projects: choose 1–3 phases."}
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="mt-12">
              <div className="text-indigo-300 font-semibold mb-2">Step 2 | Project Committee</div>
              <div className="text-xs text-white/60 mb-4">Select the type of officer you are</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {officerTypes.map((item) => (
                  <button
                    type="button"
                    key={item.value}
                    onClick={() => setOfficerType(item.value)}
                    className={`p-4 flex flex-col items-center justify-center rounded-xl cursor-pointer shadow-lg bg-[#0a1324] border border-transparent hover:ring-1 hover:ring-indigo-400/30 ${officerType === item.value ? "bg-indigo-600/20 ring-1 ring-indigo-400/50" : ""}`}
                  >
                    <span>{item.icon}</span>
                    <span className="font-bold mt-2 text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
              {!officerType && <div className="text-xs text-red-400 mt-2">Please select an officer type</div>}
            </div>

            {/* Submit */}
            <div className="flex justify-end mt-10">
              <button
                type="submit"
                disabled={!formValid || submitting}
                className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 px-6 py-3 rounded-xl text-white font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Creating…" : "Create Project"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
